import React, { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, Building2, X, MessageSquare, CheckCircle2, XCircle, Wallet, Filter, Layers } from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import Pagination from "../../../components/pagination";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { formatCurrency } from "../../../utils/function";
import { OrganizationsListResponse, OrgSummary } from "../businesses/types";
import {
  KEY_TYPE_OPTIONS,
  KEY_TYPE_STYLES,
  MESSAGE_STATUS_OPTIONS,
  MESSAGE_STATUS_STYLES,
  TransactionalMessage,
  TransactionalReportFilters,
  TransactionalReportResponse,
  formatReportDate,
} from "./types";

const EMPTY_FILTERS: TransactionalReportFilters = {};

// A single metric tile — same shape used across the other Global Admin
// pages (Businesses/Resellers), duplicated locally per this feature's own
// convention rather than shared.
const MetricCard: React.FC<{ icon: React.ReactNode; iconClassName: string; label: string; value: React.ReactNode; sublabel?: string }> = ({
  icon,
  iconClassName,
  label,
  value,
  sublabel,
}) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 shadow-sm min-w-0">
    <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${iconClassName}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 truncate">{value}</p>
      {sublabel && <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{sublabel}</p>}
    </div>
  </div>
);

// Searchable business filter — GET /admin/organizations?search=. Shows the
// org's name but the picker tracks/returns its id; clearing goes back to
// the platform-wide (all organizations) view.
const OrgFilterPicker: React.FC<{
  selected: OrgSummary | null;
  onSelect: (org: OrgSummary | null) => void;
}> = ({ selected, onSelect }) => {
  const token = localStorage.getItem("authToken");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { makeRequest: getOrgs } = useRequest("/admin/organizations", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const fetchOrgs = async () => {
      setLoading(true);
      const params: Record<string, unknown> = { page: 1, limit: 20 };
      if (debouncedQuery) params.search = debouncedQuery;
      const [response] = (await getOrgs(undefined, params)) as [OrganizationsListResponse, number];
      setOrgs(response?.status ? response.organizations : []);
      setLoading(false);
    };
    fetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTyping = (value: string) => {
    setQuery(value);
    if (selected) onSelect(null);
    if (!open) setOpen(true);
  };

  return (
    <div className="relative w-full sm:w-72" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
        <input
          value={selected ? selected.name : query}
          onChange={(e) => handleTyping(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="All organizations"
          className="w-full pl-9 pr-16 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {selected ? (
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setQuery("");
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-300"
            title="Clear — show all organizations"
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown
            size={14}
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {loading ? (
            <div className="px-3 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">Searching…</div>
          ) : orgs.length === 0 ? (
            <div className="px-3 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 text-center">No businesses match your search.</div>
          ) : (
            orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => {
                  onSelect(org);
                  setQuery("");
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                  <Building2 size={12} className="text-blue-600 dark:text-blue-400" />
                </span>
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Row-click detail — everything shown is already in the row's own data, so
// this is just a richer read-only view, no extra request.
const MessageDetailsDrawer: React.FC<{ message: TransactionalMessage | null; onClose: () => void; currencySymbol: string }> = ({
  message,
  onClose,
  currencySymbol,
}) => {
  const fields: Array<[string, React.ReactNode]> = message
    ? [
        ["Message ID", message.message_id],
        ...(message.org_name ? ([["Organization", message.org_name]] as Array<[string, React.ReactNode]>) : []),
        ["To", message.to],
        ["Sender ID", message.sender_id],
        ["Status", <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${MESSAGE_STATUS_STYLES[message.status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>{message.status}</span>],
        ["Key Type", <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full uppercase ${KEY_TYPE_STYLES[message.key_type] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>{message.key_type}</span>],
        ["API Key", message.api_key_name || "—"],
        ["Batch ID", message.batch_id || "—"],
        ["Gateway Message ID", message.gateway_message_id || "—"],
        ["Error Code", message.err_code || "—"],
        ["Cost", `${currencySymbol}${message.cost.toFixed(3)}`],
        ["Billing Event", message.billing_event_id || "—"],
        ["Sent", formatReportDate(message.created_at)],
        ["Updated", formatReportDate(message.updated_at)],
      ]
    : [];

  return (
    <ReuseableDrawer isOpen={!!message} onClose={onClose} title="Message Details" initialWidth="36%">
      {message && (
        <div className="p-5 space-y-4">
          <div className="border border-gray-100 dark:border-gray-800 rounded-lg p-3.5">
            <p className="text-[11px]  text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1.5">Message</p>
            <p className="text-sm text-gray-800 dark:text-gray-100 break-words">{message.message}</p>
          </div>
          <div className="space-y-2.5">
            {fields.map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
                <span className="text-gray-800 dark:text-gray-100 text-right break-all">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ReuseableDrawer>
  );
};

// Global Admin's "see all reports and transactions" view — GET
// /admin/reports/transactional. Omitting org_id shows the platform-wide
// rollup across every business; picking one scopes everything (summary,
// messages, batches, wallet) down to that org, same endpoint either way.
const GlobalAdminTransactionalReports: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [data, setData] = useState<TransactionalReportResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [filters, setFilters] = useState<TransactionalReportFilters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<TransactionalMessage | null>(null);

  const { makeRequest: getReport } = useRequest("/admin/reports/transactional", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (page === 1) setInitialLoading(true);
      else setIsPaginationLoading(true);
      const params: Record<string, unknown> = { page, limit: pageSize, ...filters };
      if (selectedOrg) params.org_id = selectedOrg.id;
      else delete params.org_id;
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
      const [response] = (await getReport(undefined, params)) as [TransactionalReportResponse, number];
      if (response?.status) setData(response);
      setInitialLoading(false);
      setIsPaginationLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedOrg, filters]);

  // Any filter change other than paging itself resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [selectedOrg, filters]);

  const updateFilter = (key: keyof TransactionalReportFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const summary = data?.summary;
  const currencySymbol = summary?.currency_symbol || "$";
  const showOrgColumn = !selectedOrg;
  const messages = data?.messages.data || [];
  const batches = data?.batches || [];
  const wallet = data?.wallet;
  const totalPages = data?.messages.pages ?? 1;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Transactional Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Every transactional message, batch and wallet debit — across all businesses, or scoped to one.
            </p>
          </div>
          <OrgFilterPicker selected={selectedOrg} onSelect={setSelectedOrg} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            icon={<MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            iconClassName="bg-blue-50 dark:bg-blue-500/15"
            label="Total Messages"
            value={(summary?.total ?? 0).toLocaleString()}
            sublabel={summary ? `${summary.live_count.toLocaleString()} live · ${summary.test_count.toLocaleString()} test` : undefined}
          />
          <MetricCard
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
            iconClassName="bg-emerald-50 dark:bg-emerald-500/15"
            label="Delivered"
            value={(summary?.delivered ?? 0).toLocaleString()}
            sublabel={summary ? `${summary.delivered_rate}% delivery rate` : undefined}
          />
          <MetricCard
            icon={<XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />}
            iconClassName="bg-rose-50 dark:bg-rose-500/15"
            label="Failed"
            value={(summary?.failed ?? 0).toLocaleString()}
            sublabel={summary ? `${summary.failed_rate}% failure rate` : undefined}
          />
          <MetricCard
            icon={<Wallet className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            iconClassName="bg-blue-50 dark:bg-blue-500/15"
            label="Total Cost"
            value={formatCurrency(summary?.total_cost ?? 0, summary?.currency || "USD", "symbol")}
          />
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Filter size={13} /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pb-1">
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
                <input
                  value={filters.search || ""}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  placeholder="Phone number or message"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                <select
                  value={filters.status || ""}
                  onChange={(e) => updateFilter("status", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All statuses</option>
                  {MESSAGE_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s} className="capitalize">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Key Type</label>
                <select
                  value={filters.key_type || ""}
                  onChange={(e) => updateFilter("key_type", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All key types</option>
                  {KEY_TYPE_OPTIONS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Sender ID</label>
                <input
                  value={filters.sender_id || ""}
                  onChange={(e) => updateFilter("sender_id", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Batch ID</label>
                <input
                  value={filters.batch_id || ""}
                  onChange={(e) => updateFilter("batch_id", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Transaction Type</label>
                <select
                  value={filters.transaction_type || ""}
                  onChange={(e) => updateFilter("transaction_type", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All types</option>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.start_date || ""}
                  onChange={(e) => updateFilter("start_date", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.end_date || ""}
                  onChange={(e) => updateFilter("end_date", e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Messages</h2>
            <div className="overflow-x-auto border border-gray-100 dark:border-gray-800 rounded-lg">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    {showOrgColumn && <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Organization</th>}
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">To</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Sender ID</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Message</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Type</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Cost</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {initialLoading || isPaginationLoading ? (
                    <tr>
                      <td colSpan={showOrgColumn ? 8 : 7} className="text-center py-10">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                        </div>
                      </td>
                    </tr>
                  ) : messages.length === 0 ? (
                    <tr>
                      <td colSpan={showOrgColumn ? 8 : 7} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                        No messages match these filters.
                      </td>
                    </tr>
                  ) : (
                    messages.map((m) => (
                      <tr
                        key={m.message_id}
                        onClick={() => setSelectedMessage(m)}
                        className="border-b border-gray-50 dark:border-gray-800 hover:bg-blue-50/30 dark:hover:bg-blue-500/10 transition-colors cursor-pointer"
                      >
                        {showOrgColumn && (
                          <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap truncate max-w-[160px]">{m.org_name || "—"}</td>
                        )}
                        <td className="py-2.5 px-3 text-gray-800 dark:text-gray-100 whitespace-nowrap">{m.to}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{m.sender_id}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 truncate max-w-[220px]">{m.message}</td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span
                            className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${
                              MESSAGE_STATUS_STYLES[m.status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {m.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full uppercase ${KEY_TYPE_STYLES[m.key_type] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>
                            {m.key_type}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-gray-800 dark:text-gray-100 whitespace-nowrap">
                          {formatCurrency(m.cost, summary?.currency || "USD", "symbol")}
                        </td>
                        <td className="py-2.5 px-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatReportDate(m.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pt-2">
                <Pagination currentPage={page} totalPages={totalPages} pageSize={pageSize} isLoading={isPaginationLoading} onPageChange={setPage} />
              </div>
            )}
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-1.5">
              <Layers size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" /> Batches
            </h2>
            {batches.length === 0 ? (
              <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No batches for these filters.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-100 dark:border-gray-800 rounded-lg">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      {showOrgColumn && <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Organization</th>}
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Sender ID</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Total</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Delivered</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Failed</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Rate</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Cost</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.batch_id} className="border-b border-gray-50 dark:border-gray-800 last:border-b-0">
                        {showOrgColumn && (
                          <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap truncate max-w-[160px]">{b.org_name || "—"}</td>
                        )}
                        <td className="py-2.5 px-3 text-gray-800 dark:text-gray-100 whitespace-nowrap">{b.sender_id}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{b.total.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{b.delivered.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{b.failed.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{b.delivered_rate}%</td>
                        <td className="py-2.5 px-3 text-gray-800 dark:text-gray-100 whitespace-nowrap">
                          {formatCurrency(b.total_cost, b.currency || "USD", "symbol")}
                        </td>
                        <td className="py-2.5 px-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatReportDate(b.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-1.5">
              <Wallet size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" /> Wallet Transactions
              {wallet && wallet.count > 0 && <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">({wallet.count})</span>}
            </h2>
            {!wallet || wallet.transactions.length === 0 ? (
              <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                {selectedOrg ? "No wallet transactions for this business yet." : "Select a business above to see its wallet transactions."}
              </p>
            ) : (
              <div className="space-y-1.5">
                {wallet.transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between gap-3 border border-gray-100 dark:border-gray-800 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize shrink-0 ${
                          tx.transaction_type === "credit"
                            ? "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                            : "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400"
                        }`}
                      >
                        {tx.transaction_type}
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate capitalize">{tx.source?.replace(/_/g, " ")}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{formatReportDate(tx.created_at)}</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(tx.amount, tx.currency, "symbol")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <MessageDetailsDrawer message={selectedMessage} onClose={() => setSelectedMessage(null)} currencySymbol={currencySymbol} />
    </div>
  );
};

export default GlobalAdminTransactionalReports;
